/**
 * Contract Naming Utilities
 * Generates display names for contracts based on client identification and address
 */

import { getStreetAddressOnly } from './addressParser';

/**
 * Extract last name from a full name
 * @param {string} fullName - Full name (e.g., "John Smith", "Smith LLC")
 * @returns {string} Last name or entity name
 */
export const extractLastName = (fullName) => {
  if (!fullName || typeof fullName !== 'string') return '';

  const trimmed = fullName.trim();

  // Check if it's an entity (LLC, Inc, Trust, etc.)
  if (/(LLC|Inc|Corp|Trust|Ltd|Co|Partnership)/i.test(trimmed)) {
    return trimmed;
  }

  // For regular names, split and get the last word
  const parts = trimmed.split(/\s+/);
  return parts[parts.length - 1] || trimmed;
};

/**
 * Generate contract display name based on client type and party info
 * Format: "[Client Last Name] - [Street Address]"
 *
 * @param {string} clientType - 'buyer' or 'seller'
 * @param {object} partyInfo - { buyer: string, seller: string }
 * @param {string} propertyAddress - Full property address
 * @returns {object} { displayName: string, subtitle: string }
 */
export const generateContractDisplayName = (clientType, partyInfo, propertyAddress) => {
  let clientName = '';

  // Get client name based on client type
  if (clientType === 'buyer' && partyInfo?.buyer) {
    clientName = extractLastName(partyInfo.buyer);
  } else if (clientType === 'seller' && partyInfo?.seller) {
    clientName = extractLastName(partyInfo.seller);
  } else if (partyInfo?.buyer) {
    // Fallback to buyer if no client type
    clientName = extractLastName(partyInfo.buyer);
  } else if (partyInfo?.seller) {
    // Fallback to seller
    clientName = extractLastName(partyInfo.seller);
  }

  // Get street address only (no city/state/zip)
  const streetAddress = getStreetAddressOnly(propertyAddress) || propertyAddress || 'Unknown Property';

  // Combine into display name
  const displayName = clientName
    ? `${clientName} - ${streetAddress}`
    : streetAddress;

  // Subtitle shows full address with city
  const subtitle = propertyAddress || 'Unknown';

  return {
    displayName,
    subtitle
  };
};

/**
 * Generate a simple file name for contract downloads
 * Format: "[Client Last Name] - [Street Address].pdf"
 *
 * @param {string} clientType - 'buyer' or 'seller'
 * @param {object} partyInfo - { buyer: string, seller: string }
 * @param {string} propertyAddress - Full property address
 * @returns {string} Filename
 */
export const generateContractFileName = (clientType, partyInfo, propertyAddress) => {
  const { displayName } = generateContractDisplayName(clientType, partyInfo, propertyAddress);
  return `${displayName}.pdf`;
};
